import React from 'react';
import { Link } from 'react-router-dom';

function RegisterForm({ onRegister }) {
    const [formData, setFormData] = React.useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [error, setError] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);
    const [passwordHelpText, setPasswordHelpText] = React.useState('');
    const [socialLoading, setSocialLoading] = React.useState({ google: false, apple: false });

    // Password validation function
    const validatePassword = (password) => {
        const hasLowercase = /[a-z]/.test(password);
        const hasUppercase = /[A-Z]/.test(password);
        const hasNumber = /[0-9]/.test(password);
        const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password);
        const isLongEnough = password.length >= 8;
        
        const requirements = [];
        
        if (!hasLowercase) requirements.push('lowercase letter');
        if (!hasUppercase) requirements.push('uppercase letter');
        if (!hasNumber) requirements.push('number');
        if (!hasSpecial) requirements.push('special character');
        if (!isLongEnough) requirements.push('minimum 8 characters');
        
        return {
            isValid: requirements.length === 0,
            requirements
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setPasswordHelpText('');

        try {
            if (formData.password !== formData.confirmPassword) {
                throw new Error('Passwords do not match');
            }
            
            // Validate password strength
            const validation = validatePassword(formData.password);
            if (!validation.isValid) {
                const missingRequirements = validation.requirements.join(', ');
                setPasswordHelpText(`Password must contain: ${missingRequirements}`);
                throw new Error(`Password too weak. Please include ${missingRequirements}.`);
            }

            await onRegister({
                name: formData.name,
                email: formData.email,
                password: formData.password
            });
            
        } catch (err) {
            console.error('Registration error:', err);
            
            // Extract and format Supabase password errors
            if (err.message && err.message.includes('AuthWeakPasswordError')) {
                setError('Password does not meet security requirements');
                setPasswordHelpText('Password must contain uppercase, lowercase, numbers, and special characters (!@#$%^&*) and be at least 8 characters long.');
            } else {
                setError(err.message || 'Registration failed');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        
        // Clear errors when user types
        if (e.target.name === 'password' || e.target.name === 'confirmPassword') {
            setError('');
            setPasswordHelpText('');
        }
    };

    const handleGoogleLogin = async () => {
        try {
            setError('');
            setSocialLoading({ ...socialLoading, google: true });
            
            if (!window.authService) {
                throw new Error('Authentication service is not available');
            }
            
            await window.authService.loginWithGoogle();
            // The page will redirect to Google login
        } catch (err) {
            console.error('Google login error:', err);
            setError(err.message || 'Failed to sign up with Google. Please try again.');
            setSocialLoading({ ...socialLoading, google: false });
        }
    };

    const handleAppleLogin = async () => {
        try {
            setError('');
            setSocialLoading({ ...socialLoading, apple: true });
            
            if (!window.authService) {
                throw new Error('Authentication service is not available');
            }
            
            await window.authService.loginWithApple();
            // The page will redirect to Apple login
        } catch (err) {
            console.error('Apple login error:', err);
            setError(err.message || 'Failed to sign up with Apple. Please try again.');
            setSocialLoading({ ...socialLoading, apple: false });
        }
    };

    return (
        <div data-name="register-form" className="max-w-md w-full bg-white/90 backdrop-blur-md rounded-xl shadow-xl p-8">
            <h2 className="text-3xl font-bold text-center mb-8 text-gray-800">Create Account</h2>
            
            {error && (
                <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg">
                    {error}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label htmlFor="name" className="block text-gray-700 mb-2 font-medium">
                        Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                        autoComplete="name"
                        style={{ color: '#374151', backgroundColor: '#FFFFFF' }}
                    />
                </div>

                <div>
                    <label htmlFor="email" className="block text-gray-700 mb-2 font-medium">
                        Email <span className="text-red-500">*</span>
                    </label>
                    <input
                        id="email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                        autoComplete="email"
                        style={{ color: '#374151', backgroundColor: '#FFFFFF' }}
                    />
                </div>

                <div>
                    <label htmlFor="password" className="block text-gray-700 mb-2 font-medium">
                        Password <span className="text-red-500">*</span>
                    </label>
                    <input
                        id="password"
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                        minLength="8"
                        autoComplete="new-password"
                        style={{ color: '#374151', backgroundColor: '#FFFFFF' }}
                    />
                    {passwordHelpText && (
                        <p className="mt-2 text-sm text-gray-600">
                            {passwordHelpText}
                        </p>
                    )}
                </div>

                <div>
                    <label htmlFor="confirmPassword" className="block text-gray-700 mb-2 font-medium">
                        Confirm Password <span className="text-red-500">*</span>
                    </label>
                    <input
                        id="confirmPassword"
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                        minLength="8"
                        autoComplete="new-password"
                        style={{ color: '#374151', backgroundColor: '#FFFFFF' }}
                    />
                </div>

                <button
                    type="submit"
                    disabled={isLoading}
                    className="btn btn-primary w-full"
                >
                    {isLoading ? (
                        <>
                            <i className="fas fa-spinner fa-spin mr-2"></i>
                            Creating Account...
                        </>
                    ) : (
                        'Create Account'
                    )}
                </button>
            </form>

            <div className="mt-6 relative flex items-center justify-center">
                <hr className="w-full border-t border-gray-300" />
                <span className="absolute bg-white px-4 text-sm text-gray-500">or continue with</span>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
                <button 
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={socialLoading.google || isLoading}
                    className="flex items-center justify-center gap-2 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    {socialLoading.google ? (
                        <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="24px" height="24px">
                            <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
                            <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
                            <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
                            <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
                        </svg>
                    )}
                    <span>Google</span>
                </button>
                <button 
                    type="button"
                    onClick={handleAppleLogin}
                    disabled={socialLoading.apple || isLoading}
                    className="flex items-center justify-center gap-2 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    {socialLoading.apple ? (
                        <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24px" height="24px">
                            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.26 2.32-2.11 4.42-3.74 4.25z"/>
                        </svg>
                    )}
                    <span>Apple</span>
                </button>
            </div>

            <div className="mt-6 text-center">
                <Link 
                    to="/login"
                    className="text-green-600 hover:text-green-700 font-medium"
                >
                    Already have an account? Sign In
                </Link>
            </div>
        </div>
    );
}

export default RegisterForm;
